import React, { useState, useEffect } from 'react';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import { TaskMaterial, MaterialSource } from '@models/warehouse.model';
import { useReturnTaskMaterial } from '@hooks/api/warehouse';
import WarehouseIcon from '@mui/icons-material/Warehouse';

interface ReturnMaterialModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskMaterial: TaskMaterial | null;
  onSuccess?: () => void;
}

export const ReturnMaterialModal: React.FC<ReturnMaterialModalProps> = ({
  isOpen,
  onClose,
  taskMaterial,
  onSuccess,
}) => {
  const [quantity, setQuantity] = useState('');
  const [notes, setNotes] = useState('');

  const returnMaterial = useReturnTaskMaterial();

  // Reset form on open
  useEffect(() => {
    if (isOpen) {
      setQuantity('');
      setNotes('');
    }
  }, [isOpen]);

  const quantityNum = parseFloat(quantity) || 0;
  const maxQuantity = taskMaterial?.quantity || 0;
  const isQuantityValid = quantityNum > 0 && quantityNum <= maxQuantity;
  const canSubmit =
    taskMaterial &&
    taskMaterial.source === MaterialSource.WAREHOUSE &&
    isQuantityValid &&
    !returnMaterial.isPending;

  const handleSubmit = async () => {
    if (!canSubmit || !taskMaterial) return;

    await returnMaterial.mutateAsync({
      taskMaterialId: taskMaterial.taskMaterialId,
      quantity: quantityNum,
      notes: notes.trim() || undefined,
    });

    onSuccess?.();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Return Material to Warehouse"
      confirmText="Return Material"
      handleConfirm={handleSubmit}
      isLoading={returnMaterial.isPending}
      disableConfirm={!canSubmit}
      size="small">
      <div className="flex flex-col gap-5">
        {/* Material Info */}
        {taskMaterial && (
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <WarehouseIcon className="text-indigo-600" sx={{ fontSize: 20 }} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">{taskMaterial.name}</p>
              <p className="text-xs text-gray-500">
                {taskMaterial.warehouse?.name || 'Warehouse'}
                {' · '}Current qty: {taskMaterial.quantity} {taskMaterial.unit || 'units'}
              </p>
            </div>
          </div>
        )}

        {/* Return Quantity */}
        <FormGroup label="Quantity to Return" htmlFor="returnQty">
          <Input
            id="returnQty"
            type="number"
            min="1"
            max={maxQuantity}
            step="1"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder={`Max: ${maxQuantity}`}
            error={quantity !== '' && !isQuantityValid}
          />
          {quantity !== '' && !isQuantityValid && (
            <p className="text-xs text-red-500 mt-1">
              {quantityNum <= 0
                ? 'Quantity must be greater than 0'
                : `Cannot return more than assigned (${maxQuantity})`}
            </p>
          )}
        </FormGroup>

        {/* Notes */}
        <FormGroup label="Notes (optional)" htmlFor="returnNotes">
          <textarea
            id="returnNotes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Reason for return..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
            rows={3}
          />
        </FormGroup>

        {/* Cost impact */}
        {taskMaterial && quantityNum > 0 && (
          <div className="p-3 bg-blue-50 rounded-md border border-blue-200">
            <p className="text-sm text-blue-700">
              Returning {quantityNum} {taskMaterial.unit || 'units'} will restore stock to the
              warehouse and reduce task material cost by{' '}
              <strong>${(quantityNum * (taskMaterial.costPerUnit || 0)).toFixed(2)}</strong>.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ReturnMaterialModal;
